import { AnimatePresence, motion } from "framer-motion";
import {
  ChevronDown,
  ChevronUp,
  Columns,
  ExternalLink,
  Eye,
  EyeOff,
  Info,
  PanelLeft,
  PanelLeftClose,
  SlidersHorizontal,
  X,
} from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";
import BaseModal from "./ui/BaseModal";
import clsx from "clsx";
import { useDeviationItem } from "../hooks/useDeviationItem";
import Select from "./ui/Select";
import Input from "./ui/Input";
import Textarea from "./ui/Textarea";
import Button from "./ui/Button";
import { statusOptionsConfig } from "./deviationItemHelpers";
import { NavLink } from "react-router";
import { BrainRegionText } from "../DiagnosticsPatternDetails";

/*
// Simple legend for highlighting semantics
const Legend = () => (
  <div className="flex flex-wrap items-center gap-2 mb-3 text-xs">
    <span
      className="px-2 py-0.5 rounded"
      style={{ background: "#ffecec", border: "1px solid #fca5a5" }}
    >
      Amplitude exceeded
    </span>
    <span
      className="px-2 py-0.5 rounded"
      style={{ background: "#ecffec", border: "1px solid #86efac" }}
    >
      Percentage exceeded
    </span>
  </div>
);
*/

const HeatmapVisualization = ({
  heatmapRawData,
  analysisDeviations,
  channelsForTableRows,
  heatmapBandOrder,
  displayedDeviationsLookup,
  displayMetric,
  setDisplayMetric,
  percentageDeviationThreshold,
  onSaveDeviation,
  onUpdateDiagnosticsCenter,
  suggestedInterpretationThemes,
  isLoadingAnalysis,
  sessionRatios = [],
  isLoadingRatios = false,
  ratioError = null,
  selectedNormativeProfileName = "",
  requireFullCoverage = false,
  setRequireFullCoverage = () => {},
  isLeftSidebarHidden = false,
  setIsLeftSidebarHidden = () => {},
}) => {
  const [showModal, setShowModal] = useState(false);
  const [modalData, setModalData] = useState({
    id: "",
    band: "",
    channel: "",
    cellTooltipText: "",
    patientAmplitude: 0,
    patientRhythm: 0,
    zScoreAmpltiude: 0,
    zScorePercentage: 0,
    status: "",
    brainArea: "",
  });
  const [selectedDeviationID, setSelectedDeviationID] = useState({});
  const deviation = useMemo(
    () =>
      analysisDeviations.find(
        (analysisDeviation) => analysisDeviation.id === selectedDeviationID
      ),
    [analysisDeviations, selectedDeviationID]
  );
  const [hoveredThemeId, setHoveredThemeId] = useState(null);
  const [selectedThemeId, setSelectedThemeId] = useState("");
  const [suggestedThemesVisibility, setSuggestedThemesVisibility] =
    useState(false);
  const [showThemeDetailsModal, setShowThemeDetailsModal] = useState(false);
  const [visibleThemesCount, setVisibleThemesCount] = useState(5);
  const THEMES_PAGE_SIZE = 5;
  const [selectedThemeForModal, setSelectedThemeForModal] = useState(null);
  const [highlightPercentAmplitude, setHighlightPercentAmplitude] = useState(25);
  const [highlightPercentPercentage, setHighlightPercentPercentage] = useState(25);
  const [showHighlightControls, setShowHighlightControls] = useState(false);
  const [showNormativeReference, setShowNormativeReference] = useState(false);
  const [showRatioComparison, setShowRatioComparison] = useState(false);

  // Handler for toggling ratio comparison mode
  const handleToggleRatioComparison = () => {
    const newValue = !showRatioComparison;
    setShowRatioComparison(newValue);
    if (newValue) {
      // When enabling ratio comparison, hide sidebar and themes
      setIsLeftSidebarHidden(true);
      setSuggestedThemesVisibility(false);
    } else {
      // When exiting ratio comparison, restore the sidebar
      setIsLeftSidebarHidden(false);
    }
  };

  const {
    isEditing,
    editData,
    interpretationMatches,
    isLoadingInterpretationsMatches,
    handleEdit,
    handleCancelEdit,
    handleInputChange,
    handleSave,
    fetchDiagnosticsMatches,
    fetchInterpretationMatches,
  } = useDeviationItem(
    deviation,
    onSaveDeviation,
    onUpdateDiagnosticsCenter,
    false
  );

  useEffect(() => {
    fetchDiagnosticsMatches();
    fetchInterpretationMatches();
  }, [deviation, fetchDiagnosticsMatches, fetchInterpretationMatches]);

  // Create structured data for ratio heatmap table
  // Columns = ratio labels, Rows = channels (same order as main heatmap)
  const ratioHeatmapData = useMemo(() => {
    if (!Array.isArray(sessionRatios) || sessionRatios.length === 0) {
      return { labels: [], channelData: new Map() };
    }

    // Get unique ratio labels in a consistent order
    const labelSet = new Set();
    sessionRatios.forEach((r) => {
      const label = r?.ratio_label || r?.ratio_name;
      if (label) labelSet.add(label);
    });
    const labels = Array.from(labelSet);

    // Build a map: channel -> { ratio_label -> ratio_object }
    const channelData = new Map();
    sessionRatios.forEach((ratio) => {
      const channel = ratio?.channel;
      const label = ratio?.ratio_label || ratio?.ratio_name;
      if (!channel || !label) return;

      if (!channelData.has(channel)) {
        channelData.set(channel, new Map());
      }
      channelData.get(channel).set(label, ratio);
    });

    return { labels, channelData };
  }, [sessionRatios]);

  const fallbackDisplayedLookup =
    displayedDeviationsLookup instanceof Set
      ? displayedDeviationsLookup
      : new Set();

  // Cache normalized severity values for each cell/band pair so table rendering stays cheap.
  const severityLookup = useMemo(() => {
    if (!Array.isArray(heatmapRawData)) {
      return new Map();
    }

    return heatmapRawData.reduce((acc, cell) => {
      const channelKey = (cell?.channel || "").toUpperCase();
      const bandKey = (cell?.frequency_band || "").toLowerCase();
      if (!channelKey || !bandKey) {
        return acc;
      }

      const amplitudeSeverity = Number(cell?.amplitude_severity);
      const percentageSeverity = Number(cell?.percentage_severity);

      acc.set(`${channelKey}-${bandKey}`, {
        amplitude: {
          severity: Number.isFinite(amplitudeSeverity) ? amplitudeSeverity : 0,
          exceeds: Boolean(cell?.is_amplitude_deviation),
          direction:
            cell?.amplitude_deviation_direction ||
            (amplitudeSeverity > 0
              ? "high"
              : amplitudeSeverity < 0
                ? "low"
                : null),
        },
        percentage: {
          severity: Number.isFinite(percentageSeverity) ? percentageSeverity : 0,
          exceeds: Boolean(cell?.is_percentage_deviation),
          direction:
            cell?.percentage_deviation_direction ||
            (percentageSeverity > 0
              ? "high"
              : percentageSeverity < 0
                ? "low"
                : null),
        },
      });
      return acc;
    }, new Map());
  }, [heatmapRawData]);

  const normativeSummary = useMemo(() => {
    const summary = { amplitude: {}, percentage: {} };
    if (!Array.isArray(heatmapRawData)) {
      return summary;
    }

    heatmapRawData.forEach((cell) => {
      const bandKey = (cell?.frequency_band || "").toLowerCase();
      if (!bandKey) return;

      if (!summary.amplitude[bandKey]) {
        const minVal = Number.isFinite(cell?.normative_amplitude_min)
          ? Number(cell.normative_amplitude_min)
          : null;
        const maxVal = Number.isFinite(cell?.normative_amplitude_max)
          ? Number(cell.normative_amplitude_max)
          : null;
        const targetVal = Number.isFinite(cell?.norm_amplitude)
          ? Number(cell.norm_amplitude)
          : null;

        if (minVal !== null || maxVal !== null || targetVal !== null) {
          summary.amplitude[bandKey] = {
            min: minVal,
            max: maxVal,
            target: targetVal,
            direction: cell?.normative_amplitude_direction || null,
          };
        }
      }

      if (!summary.percentage[bandKey]) {
        const targetVal = Number.isFinite(cell?.norm_percentage)
          ? Number(cell.norm_percentage)
          : null;
        const minVal = Number.isFinite(cell?.normative_percentage_min)
          ? Number(cell.normative_percentage_min)
          : null;
        const maxVal = Number.isFinite(cell?.normative_percentage_max)
          ? Number(cell.normative_percentage_max)
          : null;
        const toleranceVal = Number.isFinite(cell?.normative_percentage_tolerance)
          ? Number(cell.normative_percentage_tolerance)
          : null;

        if (targetVal !== null || minVal !== null || maxVal !== null) {
          summary.percentage[bandKey] = {
            target: targetVal,
            min: minVal,
            max: maxVal,
            tolerance: toleranceVal,
          };
        }
      }
    });

    return summary;
  }, [heatmapRawData]);

  const hasSeverityData = severityLookup.size > 0;

  // Clamped highlight percentages for each metric
  const clampedHighlightPercentAmplitude = useMemo(() => {
    const numericValue = Number(highlightPercentAmplitude);
    if (!Number.isFinite(numericValue)) return 0;
    return Math.min(Math.max(numericValue, 0), 100);
  }, [highlightPercentAmplitude]);

  const clampedHighlightPercentPercentage = useMemo(() => {
    const numericValue = Number(highlightPercentPercentage);
    if (!Number.isFinite(numericValue)) return 0;
    return Math.min(Math.max(numericValue, 0), 100);
  }, [highlightPercentPercentage]);

  // Severity entries for amplitude
  const severityEntriesForAmplitude = useMemo(() => {
    if (!hasSeverityData) return [];
    const entries = [];
    severityLookup.forEach((metricScores, key) => {
      const metricEntry = metricScores?.amplitude;
      if (metricEntry?.exceeds && Number.isFinite(metricEntry.severity)) {
        entries.push({ key, score: Math.abs(metricEntry.severity) });
      }
    });
    return entries;
  }, [severityLookup, hasSeverityData]);

  // Severity entries for percentage
  const severityEntriesForPercentage = useMemo(() => {
    if (!hasSeverityData) return [];
    const entries = [];
    severityLookup.forEach((metricScores, key) => {
      const metricEntry = metricScores?.percentage;
      if (metricEntry?.exceeds && Number.isFinite(metricEntry.severity)) {
        entries.push({ key, score: Math.abs(metricEntry.severity) });
      }
    });
    return entries;
  }, [severityLookup, hasSeverityData]);

  const hasAmplitudeDeviations = severityEntriesForAmplitude.length > 0;
  const hasPercentageDeviations = severityEntriesForPercentage.length > 0;
  const hasAnyDeviations = hasAmplitudeDeviations || hasPercentageDeviations;

  // Top deviation lookups for each metric
  const topDeviationLookupAmplitude = useMemo(() => {
    if (!hasAmplitudeDeviations || clampedHighlightPercentAmplitude === 0) {
      return new Set();
    }
    const sortedEntries = [...severityEntriesForAmplitude].sort((a, b) => b.score - a.score);
    const topCount = Math.max(1, Math.ceil((sortedEntries.length * clampedHighlightPercentAmplitude) / 100));
    return new Set(sortedEntries.slice(0, topCount).map((entry) => entry.key));
  }, [severityEntriesForAmplitude, clampedHighlightPercentAmplitude, hasAmplitudeDeviations]);

  const topDeviationLookupPercentage = useMemo(() => {
    if (!hasPercentageDeviations || clampedHighlightPercentPercentage === 0) {
      return new Set();
    }
    const sortedEntries = [...severityEntriesForPercentage].sort((a, b) => b.score - a.score);
    const topCount = Math.max(1, Math.ceil((sortedEntries.length * clampedHighlightPercentPercentage) / 100));
    return new Set(sortedEntries.slice(0, topCount).map((entry) => entry.key));
  }, [severityEntriesForPercentage, clampedHighlightPercentPercentage, hasPercentageDeviations]);

  const allValuesWithinNorm = hasSeverityData && !hasAnyDeviations;

  const formatNumber = (value, digits = 2) => {
    const num = Number(value);
    return Number.isFinite(num) ? num.toFixed(digits) : "—";
  };

  const formatAmplitudeNorm = (bandKey) => {
    const norm = normativeSummary.amplitude[bandKey.toLowerCase()];
    if (!norm) {
      return "—";
    }

    const minPart = norm.min !== null && norm.min !== undefined ? formatNumber(norm.min, 0) : null;
    const maxPart = norm.max !== null && norm.max !== undefined ? formatNumber(norm.max, 0) : null;
    const targetPart = norm.target !== null && norm.target !== undefined ? formatNumber(norm.target, 0) : null;

    if (minPart && maxPart) {
      if (minPart === maxPart) {
        return minPart;
      }
      return `${minPart}–${maxPart}`;
    }
    if (maxPart) {
      return `≤${maxPart}`;
    }
    if (minPart) {
      return `≥${minPart}`;
    }
    if (targetPart) {
      return targetPart;
    }
    return "—";
  };

  const formatPercentageNorm = (bandKey) => {
    const norm = normativeSummary.percentage[bandKey.toLowerCase()];
    if (!norm) {
      return "—";
    }

    const minPart = norm.min !== null && norm.min !== undefined ? formatNumber(norm.min, 0) : null;
    const maxPart = norm.max !== null && norm.max !== undefined ? formatNumber(norm.max, 0) : null;
    const targetPart = norm.target !== null && norm.target !== undefined ? formatNumber(norm.target, 0) : null;
    const tolerancePart = norm.tolerance !== null && norm.tolerance !== undefined ? formatNumber(norm.tolerance, 0) : null;

    // If we have distinct min and max, show range
    if (minPart && maxPart && minPart !== maxPart) {
      return `${minPart}–${maxPart}`;
    }
    // If min and max are the same (legacy: target only), show with tolerance if available
    if (targetPart) {
      if (tolerancePart) {
        return `${targetPart}±${tolerancePart}`;
      }
      return targetPart;
    }
    if (maxPart) {
      return `≤${maxPart}`;
    }
    if (minPart) {
      return `≥${minPart}`;
    }
    return "—";
  };

  const normativeProfileDisplayName =
    selectedNormativeProfileName || "Selected profile";
  const hasNormativeReferenceData = useMemo(() => {
    if (!Array.isArray(heatmapBandOrder)) return false;
    return heatmapBandOrder.some((band) => {
      const key = typeof band === "string" ? band.toLowerCase() : null;
      if (!key) return false;
      // Check if either amplitude or percentage norms exist
      return Boolean(normativeSummary.amplitude[key]) || Boolean(normativeSummary.percentage[key]);
    });
  }, [heatmapBandOrder, normativeSummary]);

  // Get background color for ratio heatmap cells based on status
  const getRatioCellBgColor = (status) => {
    switch (status) {
      case "above_max":
      case "outside_target":
        return "#fee2e2"; // red-100 - above threshold
      case "below_min":
        return "#dbeafe"; // blue-100 - below threshold
      default:
        return "transparent"; // no color for in_range, near_target, or missing
    }
  };

  // console.log({
  //   heatmapRawData,
  //   heatmapBandOrder,
  //   channelsForTableRows,
  //   displayedDeviationsLookup,
  // });
  const handleCellClick = (bandData, channelData, cellTooltipText) => {
    const cellRawData = heatmapRawData?.find(
      (data) =>
        data.channel === channelData &&
        data.frequency_band === bandData.toLowerCase()
    );
    const cellDeviationData = analysisDeviations?.find(
      (data) =>
        data.channel === channelData &&
        data.frequency_band === bandData.toLowerCase()
    );

    if (!cellRawData || !cellDeviationData) {
      return;
    }

    setSelectedDeviationID(cellDeviationData.id);
    setModalData({
      id: cellDeviationData.id,
      band: bandData,
      channel: channelData,
      cellTooltipText: cellTooltipText,
      patientAmplitude: cellRawData.amplitude,
      patientRhythm: cellRawData.percentage,
      normAmplitude: cellRawData.norm_amplitude,
      normPercentage: cellRawData.norm_percentage,
      amplitudeMin: cellRawData.normative_amplitude_min,
      amplitudeMax: cellRawData.normative_amplitude_max,
      percentageMin: cellRawData.normative_percentage_min,
      percentageMax: cellRawData.normative_percentage_max,
      status: cellDeviationData.status,
      brainArea: cellDeviationData.brain_area,
      measureType: cellDeviationData.measure_type,
      clinicianInterpretation: cellDeviationData.clinician_interpretation,
      diagnosisCategory: cellDeviationData.diagnosis_category,
    });
    setShowModal((prev) => !prev);
  };

  // Function to generate tooltip parts based on cell data
  const generateTooltipParts = (bandDataObject) => {
    const tooltipParts = [];

    // Check if it's the ideal data structure
    if (
      typeof bandDataObject === "object" &&
      bandDataObject.amplitude !== undefined &&
      bandDataObject.percentage !== undefined &&
      bandDataObject.amplitude_magnitude !== undefined
    ) {
      tooltipParts.push(
        `Patient Amplitude: ${parseFloat(bandDataObject.amplitude).toFixed(
          2
        )} µV`,
        `Patient Rhythm %: ${parseFloat(bandDataObject.percentage).toFixed(
          2
        )} %`,
        `Z-Score (Amplitude): ${parseFloat(
          bandDataObject.amplitude_magnitude
        ).toFixed(2)}`
      );

      if (bandDataObject.percentage_magnitude !== undefined) {
        tooltipParts.push(
          `Z-Score (Percentage): ${parseFloat(
            bandDataObject.percentage_magnitude
          ).toFixed(2)}`
        );
      }

      if (bandDataObject.percent_deviation_power !== undefined) {
        tooltipParts.push(
          `Power Deviation: ${parseFloat(
            bandDataObject.percent_deviation_power
          ).toFixed(1)}%`
        );
      }

      if (
        bandDataObject.normative_amplitude_min !== undefined &&
        bandDataObject.normative_amplitude_max !== undefined
      ) {
        tooltipParts.push(
          `Normative Amplitude: ${parseFloat(
            bandDataObject.normative_amplitude_min
          ).toFixed(1)}-${parseFloat(
            bandDataObject.normative_amplitude_max
          ).toFixed(1)} µV`
        );
      }

      if (
        bandDataObject.normative_rhythm_percentage_min !== undefined &&
        bandDataObject.normative_rhythm_percentage_max !== undefined
      ) {
        tooltipParts.push(
          `Normative Rhythm %: ${parseFloat(
            bandDataObject.normative_rhythm_percentage_min
          ).toFixed(1)}-${parseFloat(
            bandDataObject.normative_rhythm_percentage_max
          ).toFixed(1)}%`
        );
      }
    }
    // Fallback for structure with amplitude & z-score, but missing patient_rhythm_percentage
    else if (
      typeof bandDataObject === "object" &&
      bandDataObject.amplitude !== undefined &&
      bandDataObject.amplitude_magnitude !== undefined
    ) {
      tooltipParts.push(
        `Amplitude: ${parseFloat(bandDataObject.amplitude).toFixed(2)} µV`,
        `Z-Score: ${parseFloat(bandDataObject.amplitude_magnitude).toFixed(2)}`
      );

      if (bandDataObject.percent_deviation !== undefined) {
        tooltipParts.push(
          `Power Deviation: ${parseFloat(
            bandDataObject.percent_deviation
          ).toFixed(1)}%`
        );
      }

      tooltipParts.push("(Patient rhythm % data missing for cell display)");
    }
    // Fallback for original structure (just z-score)
    else if (typeof bandDataObject === "number" && !isNaN(bandDataObject)) {
      tooltipParts.push(
        `Z-Score: ${parseFloat(bandDataObject).toFixed(
          2
        )} (Detailed data missing)`
      );
    }

    return tooltipParts;
  };

  // Function to render split cell content with both amplitude and percentage
  // themeHighlight: { amplitude: 'exceeds'|'within'|null, percentage: 'exceeds'|'within'|null }
  const renderSplitCellContent = (bandDataObject, amplitudeBgColor, percentageBgColor, emphasizeAmplitude, emphasizePercentage, themeHighlight = {}) => {
    const amplitudeValue = bandDataObject?.amplitude;
    const percentageValue = bandDataObject?.percentage;

    const hasAmplitude = amplitudeValue !== undefined && amplitudeValue !== null;
    const hasPercentage = percentageValue !== undefined && percentageValue !== null;

    if (!hasAmplitude && !hasPercentage) {
      return <div className="text-gray-400 text-center py-1.5 text-[11px]">N/A</div>;
    }

    // Generate inset box-shadow for theme highlighting
    // 'exceeds' = red ring, 'within' = blue ring
    const getHighlightStyle = (highlightType) => {
      if (!highlightType) return {};
      const color = highlightType === "exceeds" ? "#ef4444" : "#3b82f6";
      return {
        boxShadow: `inset 0 0 0 3px ${color}`,
        borderRadius: "4px",
      };
    };

    const amplitudeHighlightStyle = getHighlightStyle(themeHighlight?.amplitude);
    const percentageHighlightStyle = getHighlightStyle(themeHighlight?.percentage);

    return (
      <div className="flex text-[11px]">
        {/* Amplitude half (left) */}
        <div
          className="flex-1 px-1 py-1.5 border-r border-gray-200 text-center"
          style={{ backgroundColor: amplitudeBgColor, minWidth: '28px', ...amplitudeHighlightStyle }}
        >
          <div className={emphasizeAmplitude ? "font-bold" : "font-normal"}>
            {hasAmplitude ? `${parseFloat(amplitudeValue).toFixed(2)}` : "—"}
          </div>
        </div>
        {/* Percentage half (right) */}
        <div
          className="flex-1 px-1 py-1.5 text-center"
          style={{ backgroundColor: percentageBgColor, minWidth: '28px', ...percentageHighlightStyle }}
        >
          <div className={emphasizePercentage ? "font-bold" : "font-normal"}>
            {hasPercentage ? `${parseFloat(percentageValue).toFixed(2)}` : "—"}
          </div>
        </div>
      </div>
    );
  };

  const computeSeverityColor = (severityValue, metric) => {
    if (!Number.isFinite(severityValue) || severityValue === 0) {
      return "#ffffff";
    }
    const absSeverity = Math.abs(severityValue);
    const isSoftPercentage = metric === "percentage" && absSeverity <= 0.1;

    if (isSoftPercentage) {
      const alpha = 0.04 + (absSeverity / 0.1) * 0.08; // 0.04 - 0.12 for near-norm values
      return severityValue > 0
        ? `rgba(248, 113, 113, ${alpha.toFixed(2)})`
        : `rgba(96, 165, 250, ${alpha.toFixed(2)})`;
    }

    const magnitude = Math.min(absSeverity, 1.5);
    const alpha = 0.15 + (magnitude / 1.5) * 0.45;
    return severityValue > 0
      ? `rgba(248, 113, 113, ${alpha.toFixed(2)})`
      : `rgba(96, 165, 250, ${alpha.toFixed(2)})`;
  };

  // Function to process cell data and get all necessary values
  const processCellData = (channelKey, bandKey, x, y, bands, channels) => {
    const cellKeyForLookup = `${channelKey}-${bandKey}`;
    const severityEntry = severityLookup.get(cellKeyForLookup);
    
    // Get severity data for BOTH metrics
    const amplitudeEntry = severityEntry?.amplitude;
    const percentageEntry = severityEntry?.percentage;
    const amplitudeSeverity = amplitudeEntry?.severity ?? 0;
    const percentageSeverity = percentageEntry?.severity ?? 0;
    const hasAmplitudeDeviation = Boolean(amplitudeEntry?.exceeds);
    const hasPercentageDeviation = Boolean(percentageEntry?.exceeds);
    
    // For emphasis, check each metric's top deviation lookup separately
    const shouldEmphasizeAmplitude = hasSeverityData
      ? hasAmplitudeDeviation && topDeviationLookupAmplitude.has(cellKeyForLookup)
      : fallbackDisplayedLookup.has(cellKeyForLookup);
    const shouldEmphasizePercentage = hasSeverityData
      ? hasPercentageDeviation && topDeviationLookupPercentage.has(cellKeyForLookup)
      : fallbackDisplayedLookup.has(cellKeyForLookup);
    const shouldEmphasizeCell = shouldEmphasizeAmplitude || shouldEmphasizePercentage;

    const bandDataObject = heatmapRawData?.find(
      (cellData) =>
        cellData.channel?.toUpperCase() === channelKey?.toUpperCase() &&
        cellData.frequency_band?.toLowerCase() === bandKey?.toLowerCase()
    );

    const deviationObj = analysisDeviations?.find(
      (cellData) =>
        cellData.channel?.toUpperCase() === channelKey?.toUpperCase() &&
        cellData.frequency_band?.toLowerCase() === bandKey?.toLowerCase()
    );
    const status = deviationObj?.status;

    let cellTooltipText = "No data available";

    if (bandDataObject !== undefined && bandDataObject !== null) {
      try {
        const tooltipParts = generateTooltipParts(bandDataObject);
        if (tooltipParts.length > 0) {
          cellTooltipText = tooltipParts.join("\n");
        }
      } catch (err) {
        console.error("Error generating tooltip", err);
        cellTooltipText = "Data parsing error";
      }
    }

    const cellTextColor =
      status === "diagnosed"
        ? "#15803d"
        : status === "irrelevant"
          ? "#4b5563"
          : "#000000";
    
    // Compute background colors for BOTH amplitude and percentage halves independently
    let amplitudeBgColor = "transparent";
    let percentageBgColor = "transparent";
    
    if (hasSeverityData) {
      if (hasAmplitudeDeviation) {
        amplitudeBgColor = computeSeverityColor(amplitudeSeverity, "amplitude");
      }
      if (hasPercentageDeviation) {
        percentageBgColor = computeSeverityColor(percentageSeverity, "percentage");
      }
    } else if (fallbackDisplayedLookup.has(cellKeyForLookup)) {
      amplitudeBgColor = "#ffecec";
      percentageBgColor = "#ffecec";
    }
    
    // Override with status-based bg if set (applies to whole cell)
    if (status === "diagnosed") {
      amplitudeBgColor = "#dcfce7";
      percentageBgColor = "#dcfce7";
    } else if (status === "irrelevant") {
      amplitudeBgColor = "#e5e7eb";
      percentageBgColor = "#e5e7eb";
    }

    // Cell background for the overall cell (used for borders, not halves)
    let cellBgColor = "transparent";

    // Theme highlighting - determine which half-cells to highlight and with what color
    // This is metric-specific: only highlight MV half when displayMetric=amplitude, % when percentage
    let themeHighlight = {
      amplitude: null, // null = no highlight, 'exceeds' = red, 'within' = blue
      percentage: null,
    };

    // Show theme highlights when hovering/selecting a theme
    if (
      suggestedThemesVisibility &&
      suggestedInterpretationThemes.length > 0 &&
      (hoveredThemeId || selectedThemeId)
    ) {
      const activeTheme = suggestedInterpretationThemes.find(
        (it) => it.theme_id === (hoveredThemeId ?? selectedThemeId)
      );

      if (activeTheme) {
        const allCombos =
          activeTheme.all_combinations ||
          activeTheme.exceeding_combinations ||
          [];
        const matchingCombo = allCombos.find(
          (combo) =>
            combo.channel?.toUpperCase() === channelKey?.toUpperCase() &&
            combo.frequency_band?.toLowerCase() === bandKey?.toLowerCase()
        );

        if (matchingCombo) {
          // Determine highlight for each metric based on actual deviation data
          // Amplitude highlight: only when displayMetric is amplitude
          if (displayMetric === "amplitude") {
            themeHighlight.amplitude = hasAmplitudeDeviation ? "exceeds" : "within";
          }
          // Percentage highlight: only when displayMetric is percentage  
          if (displayMetric === "percentage") {
            themeHighlight.percentage = hasPercentageDeviation ? "exceeds" : "within";
          }
        }
      }
    }

    const hasTooltip =
      cellTooltipText && cellTooltipText !== "No data available";
    const cellStyle = getCellStyle(
      shouldEmphasizeCell,
      cellTextColor,
      cellBgColor,
      hasTooltip
    );

    return {
      bandDataObject,
      cellTooltipText,
      cellStyle,
      amplitudeBgColor,
      percentageBgColor,
      shouldEmphasizeAmplitude,
      shouldEmphasizePercentage,
      themeHighlight,
    };
  };

  // Function to get cell styles
  const getCellStyle = (
    shouldEmphasize,
    cellTextColor,
    cellBgColor,
    hasTooltip
  ) => {
    const isMobile = window.innerWidth < 768;

    return {
      textAlign: "center",
      color: cellTextColor,
      backgroundColor: cellBgColor,
      fontWeight: shouldEmphasize ? "bold" : "normal",
      cursor: hasTooltip ? "pointer" : "default",
      padding: "0",
      fontSize: isMobile ? "0.8rem" : "0.875rem",
      verticalAlign: "middle",
      minWidth: isMobile ? "36px" : "42px",
      transition: "all 150ms ease-in-out",
    };
  };

  return (
    <motion.div
      className="bg-white p-3 md:p-6 rounded-xl border border-gray-100 shadow-sm overflow-x-auto"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.3 }}
    >
      <div className="flex items-center justify-end mb-3 md:mb-5">
        <div className="flex gap-2">
          {/* Sidebar toggle button - always visible for easy navigation */}
          <button
            onClick={() => setIsLeftSidebarHidden(prev => !prev)}
            className={`flex items-center justify-center px-3 py-2 ${
              isLeftSidebarHidden
                ? "bg-gray-600 hover:bg-gray-700 text-white"
                : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
            } rounded-lg text-sm font-medium transition-colors duration-300 cursor-pointer`}
            title={isLeftSidebarHidden ? "Show Patients & Sessions" : "Hide Patients & Sessions"}
          >
            {isLeftSidebarHidden ? (
              <PanelLeft className="w-4 h-4 mr-2 flex-shrink-0" />
            ) : (
              <PanelLeftClose className="w-4 h-4 mr-2 flex-shrink-0" />
            )}
            {isLeftSidebarHidden ? "Show Sidebar" : "Hide Sidebar"}
          </button>
          <button
            onClick={() => {
              const newVisibility = !suggestedThemesVisibility;
              setSuggestedThemesVisibility(newVisibility);
              setVisibleThemesCount(THEMES_PAGE_SIZE);
              // When showing themes, hide the sidebar. When hiding themes, show the sidebar.
              setIsLeftSidebarHidden(newVisibility);
            }}
            className={`flex items-center justify-center px-3 py-2 ${
              suggestedThemesVisibility
                ? "bg-blue-600 hover:bg-blue-700 text-white"
                : "bg-blue-50 hover:bg-blue-100 text-blue-700 border border-blue-200"
            } rounded-lg text-sm font-medium transition-colors duration-300 cursor-pointer`}
          >
            {suggestedThemesVisibility ? (
              <EyeOff className="w-4 h-4 mr-2 flex-shrink-0" />
            ) : (
              <Eye className="w-4 h-4 mr-2 flex-shrink-0" />
            )}
            {suggestedThemesVisibility ? "Hide Suggested Diagnostics" : "Show Suggested Diagnostics"}
          </button>
        </div>
      </div>

      <div className="mb-2 flex flex-wrap items-center justify-end gap-2">
        {/* Compare Ratios button - only show if ratio data exists */}
        {ratioHeatmapData.labels.length > 0 && (
          <button
            type="button"
            onClick={handleToggleRatioComparison}
            className={`flex items-center gap-1 px-3 py-2 text-sm rounded-lg border transition-colors ${
              showRatioComparison 
                ? "bg-sky-500 border-sky-500 text-white hover:bg-sky-600" 
                : "bg-white border-sky-300 text-sky-700 hover:bg-sky-50"
            }`}
            title="Extend table with ratio comparison columns"
          >
            {showRatioComparison ? (
              <X className="w-4 h-4" />
            ) : (
              <Columns className="w-4 h-4" />
            )}
            {showRatioComparison ? "Exit Ratio View" : "Compare Ratios"}
          </button>
        )}
        <button
          type="button"
          onClick={() => setShowHighlightControls((prev) => !prev)}
          className={`flex items-center gap-1 px-3 py-2 text-sm rounded-lg border transition-colors ${showHighlightControls ? "bg-blue-50 border-blue-200 text-blue-700" : "bg-white border-gray-200 text-gray-600 hover:bg-gray-50"}`}
          disabled={!hasSeverityData}
          aria-disabled={!hasSeverityData}
        >
          <SlidersHorizontal className="w-4 h-4" />
          {showHighlightControls ? "Hide" : "Show"} Highlight Slider
        </button>
        <button
          type="button"
          onClick={() => setShowNormativeReference((prev) => !prev)}
          className={`flex items-center gap-1 px-3 py-2 text-sm rounded-lg border transition-colors ${showNormativeReference ? "bg-indigo-50 border-indigo-200 text-indigo-700" : "bg-white border-gray-200 text-gray-600 hover:bg-gray-50"}`}
          aria-pressed={showNormativeReference}
          title="Toggle reference values from the selected normative profile"
        >
          <Info className="w-4 h-4" />
          {showNormativeReference ? "Hide Norms" : "Show Norms"}
        </button>
      </div>

      {showHighlightControls && (
        <div className="mb-4 grid grid-cols-1 md:grid-cols-2 gap-4" aria-live="polite">
          {/* Amplitude slider */}
          <div className="p-3 bg-blue-50 rounded-lg border border-blue-100">
            <label
              htmlFor="highlightThresholdAmplitude"
              className="block text-xs md:text-sm font-medium text-blue-700"
            >
              Amplitude: top {clampedHighlightPercentAmplitude}%
            </label>
            <div className="flex items-center gap-3 mt-1">
              <input
                id="highlightThresholdAmplitude"
                type="range"
                min="0"
                max="100"
                step="1"
                value={clampedHighlightPercentAmplitude}
                onChange={(event) => setHighlightPercentAmplitude(Number(event.target.value))}
                className="flex-1 accent-blue-600"
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={clampedHighlightPercentAmplitude}
                aria-label="Highlight top amplitude deviations"
                disabled={!hasAmplitudeDeviations}
              />
              <span className="text-sm font-semibold text-blue-700 w-12 text-right">
                {clampedHighlightPercentAmplitude}%
              </span>
            </div>
            <p className="text-[10px] text-blue-600 mt-1">
              Bold top µV values that exceed norms
            </p>
          </div>
          {/* Percentage slider */}
          <div className="p-3 bg-green-50 rounded-lg border border-green-100">
            <label
              htmlFor="highlightThresholdPercentage"
              className="block text-xs md:text-sm font-medium text-green-700"
            >
              Percentage: top {clampedHighlightPercentPercentage}%
            </label>
            <div className="flex items-center gap-3 mt-1">
              <input
                id="highlightThresholdPercentage"
                type="range"
                min="0"
                max="100"
                step="1"
                value={clampedHighlightPercentPercentage}
                onChange={(event) => setHighlightPercentPercentage(Number(event.target.value))}
                className="flex-1 accent-green-600"
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={clampedHighlightPercentPercentage}
                aria-label="Highlight top percentage deviations"
                disabled={!hasPercentageDeviations}
              />
              <span className="text-sm font-semibold text-green-700 w-12 text-right">
                {clampedHighlightPercentPercentage}%
              </span>
            </div>
            <p className="text-[10px] text-green-600 mt-1">
              Bold top % values that exceed norms
            </p>
          </div>
        </div>
      )}

      {!hasSeverityData && Array.isArray(heatmapRawData) && heatmapRawData.length > 0 && (
        <p className="mb-4 text-xs md:text-sm text-amber-700">
          Heatmap values are available but severity metadata is missing. Run a fresh analysis to enable automatic highlighting.
        </p>
      )}

      {allValuesWithinNorm && (
        <p className="mb-4 text-xs md:text-sm text-green-700 font-medium">
          All values stay within normative ranges for the currently selected metric.
        </p>
      )}

      <div className="heatmap-container w-full overflow-x-auto flex flex-col lg:flex-row">
        <AnimatePresence>
          {suggestedThemesVisibility && (
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className={`flex flex-col flex-shrink-0 transition-all duration-300 ${
                isLeftSidebarHidden 
                  ? "lg:w-80 lg:min-w-[20rem] lg:max-w-[20rem]" 
                  : "lg:w-64 lg:min-w-[16rem] lg:max-w-[16rem]"
              }`}
            >
              <div className="flex items-center justify-between mb-3">
                <h5 className="font-bold text-[#1c3f70] text-base">
                  Suggested Diagnostics{" "}
                  {suggestedInterpretationThemes.length > 0 &&
                    `(${suggestedInterpretationThemes.length})`}
                </h5>
              </div>

              {/* Metric Toggle - Amplitude vs Percentage */}
              <div className="mb-3 flex rounded-lg border border-gray-200 overflow-hidden">
                <button
                  type="button"
                  onClick={() => setDisplayMetric && setDisplayMetric("amplitude")}
                  className={`flex-1 px-3 py-2 text-sm font-medium transition-colors ${
                    displayMetric === "amplitude"
                      ? "bg-blue-600 text-white"
                      : "bg-white text-gray-600 hover:bg-gray-50"
                  }`}
                >
                  Amplitude (µV)
                </button>
                <button
                  type="button"
                  onClick={() => setDisplayMetric && setDisplayMetric("percentage")}
                  className={`flex-1 px-3 py-2 text-sm font-medium transition-colors ${
                    displayMetric === "percentage"
                      ? "bg-green-600 text-white"
                      : "bg-white text-gray-600 hover:bg-gray-50"
                  }`}
                >
                  Percentage (%)
                </button>
              </div>

              {/* Full Coverage Filter Toggle */}
              <div className="mb-3 p-2 bg-blue-50 rounded-lg border border-blue-100">
                <label className="flex items-start gap-2 cursor-pointer text-sm">
                  <input
                    type="checkbox"
                    checked={requireFullCoverage}
                    onChange={(e) => setRequireFullCoverage(e.target.checked)}
                    className="w-4 h-4 mt-0.5 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                  />
                  <span className="text-gray-700 leading-tight">
                    Show only full coverage themes
                  </span>
                </label>
              </div>

              {/* Color Legend - more compact */}
              {suggestedInterpretationThemes.length > 0 && (
                <div className="mb-3 p-2 bg-gray-50 rounded-lg text-xs">
                  <p className="text-gray-500 mb-1.5 font-medium">% of available data exceeding norm:</p>
                  <div className="flex flex-wrap gap-x-3 gap-y-1.5">
                    <div className="flex items-center gap-1.5">
                      <div className="w-3 h-3 bg-green-500 rounded-sm"></div>
                      <span className="text-gray-600">≥75%</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <div className="w-3 h-3 bg-blue-500 rounded-sm"></div>
                      <span className="text-gray-600">50-74%</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <div className="w-3 h-3 bg-amber-500 rounded-sm"></div>
                      <span className="text-gray-600">25-49%</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <div className="w-3 h-3 bg-gray-400 rounded-sm"></div>
                      <span className="text-gray-600">&lt;25%</span>
                    </div>
                  </div>
                </div>
              )}

              {suggestedInterpretationThemes.length > 0 ? (
                <div className="lg:basis-0 lg:grow lg:overflow-y-auto flex flex-col pr-2 pb-2">
                  <ul className="flex flex-col gap-3">
                    {suggestedInterpretationThemes.slice(0, visibleThemesCount).map((it, i) => {
                      // Color coding based on percentage fit
                      const percentage = it.percentage_exceeding || 0;
                      let borderColor = "";
                      let badgeColor = "";
                      let hoverColor = "";

                      if (percentage >= 75) {
                        // High confidence - Green
                        borderColor = "border-l-green-500";
                        badgeColor = "bg-green-100 text-green-800";
                        hoverColor = "hover:bg-green-50";
                      } else if (percentage >= 50) {
                        // Medium confidence - Blue
                        borderColor = "border-l-blue-500";
                        badgeColor = "bg-blue-100 text-blue-800";
                        hoverColor = "hover:bg-blue-50";
                      } else if (percentage >= 25) {
                        // Lower confidence - Amber
                        borderColor = "border-l-amber-500";
                        badgeColor = "bg-amber-100 text-amber-800";
                        hoverColor = "hover:bg-amber-50";
                      } else {
                        // Low confidence - Gray
                        borderColor = "border-l-gray-400";
                        badgeColor = "bg-gray-100 text-gray-700";
                        hoverColor = "hover:bg-gray-50";
                      }

                      const isExpanded = selectedThemeId === it.theme_id;

                      return (
                        <li
                          key={it.theme_id}
                          className={clsx(
                            "rounded-lg border border-gray-200 border-l-4 hover:shadow-md cursor-pointer transition-all",
                            borderColor,
                            hoverColor,
                            isExpanded && "bg-blue-50"
                          )}
                          onMouseOver={() => setHoveredThemeId(it.theme_id)}
                          onMouseLeave={() => setHoveredThemeId(null)}
                          onClick={() => {
                            // Toggle theme selection for heatmap highlighting and expand/collapse
                            setSelectedThemeId((_key) =>
                              _key === it.theme_id ? null : it.theme_id
                            );
                          }}
                        >
                          {/* Compact header - always visible */}
                          <div className="p-2.5 flex items-start gap-2">
                            <span
                              className={clsx(
                                "inline-flex items-center px-2 py-1 rounded text-xs font-bold flex-shrink-0 mt-0.5",
                                badgeColor
                              )}
                            >
                              {it.percentage_exceeding?.toFixed(0)}%
                            </span>
                            <p className="text-sm text-[#1c3f70] leading-snug flex-1 font-medium">
                              {it.interpretation_text}
                            </p>
                            <div className="flex items-center flex-shrink-0 gap-1">
                              {/* Info button to open modal */}
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedThemeForModal(it);
                                  setShowThemeDetailsModal(true);
                                }}
                                className="p-1 text-gray-400 hover:text-blue-600 rounded transition-colors"
                                title="View details"
                              >
                                <Info size={14} />
                              </button>
                              <NavLink
                                to={`/interpretation-themes/themes/${it.theme_id}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="p-1 text-gray-400 hover:text-[#2A5CAA] rounded transition-colors"
                                onClick={(e) => e.stopPropagation()}
                                title="Open in new tab"
                              >
                                <ExternalLink size={14} />
                              </NavLink>
                              {isExpanded ? (
                                <ChevronUp size={16} className="text-gray-400" />
                              ) : (
                                <ChevronDown size={16} className="text-gray-400" />
                              )}
                            </div>
                          </div>

                          {/* Expanded details - only shown when selected */}
                          <AnimatePresence>
                            {isExpanded && (
                              <motion.div
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: "auto", opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                transition={{ duration: 0.2 }}
                                className="overflow-hidden"
                              >
                                <div className="px-2.5 pb-2.5 flex flex-col gap-2 border-t border-gray-100 pt-2">
                                  {/* Stats row */}
                                  <div className="flex items-center gap-2 flex-wrap text-xs">
                                    <span 
                                      className="text-gray-500"
                                      title={`${it.exceeding_norm} of ${it.total_combinations} available channel-band combinations exceed the normative threshold`}
                                    >
                                      {it.exceeding_norm}/{it.total_combinations} exceed
                                    </span>
                                    {it.coverage_percentage !== undefined && it.coverage_percentage < 100 && (
                                      <span 
                                        className="text-amber-600" 
                                        title={`Only ${it.total_combinations} of ${it.theoretical_combinations || '?'} theoretical combinations are present in session data (${it.coverage_percentage?.toFixed(0)}% coverage). Missing channels: ${it.missing_channels?.join(', ') || 'none'}`}
                                      >
                                        ({it.coverage_percentage?.toFixed(0)}% coverage)
                                      </span>
                                    )}
                                  </div>

                                  {/* Missing channels warning */}
                                  {it.missing_channels && it.missing_channels.length > 0 && (
                                    <div className="flex items-center gap-1 text-xs text-amber-600 flex-wrap">
                                      <span>Missing:</span>
                                      {it.missing_channels.map((ch) => (
                                        <span key={ch} className="px-1.5 py-0.5 bg-amber-50 rounded border border-amber-200">
                                          {ch}
                                        </span>
                                      ))}
                                    </div>
                                  )}

                                  {/* Channels and Bands */}
                                  <div className="flex flex-wrap gap-1 text-xs">
                                    {[...(it.themeDefinedChannels || it.channels)].map((channel) => {
                                      const isFound = it.channels?.has(channel);
                                      return (
                                        <BrainRegionText key={channel} channel={channel}>
                                          <span className={clsx(
                                            "px-1.5 py-0.5 rounded",
                                            isFound ? "bg-gray-100" : "bg-amber-50 text-amber-600 border border-amber-200"
                                          )}>
                                            {channel}
                                            {!isFound && " ⚠"}
                                          </span>
                                        </BrainRegionText>
                                      );
                                    })}
                                    {[...it.bands].map((band) => (
                                      <span
                                        key={band}
                                        className="px-1.5 py-0.5 bg-gray-50 rounded text-gray-600"
                                      >
                                        {band}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </li>
                      );
                    })}
                  </ul>
                  
                  {/* Show More / Show Less buttons */}
                  {suggestedInterpretationThemes.length > THEMES_PAGE_SIZE && (
                    <div className="flex justify-center gap-2 mt-3 pt-2 border-t border-gray-100">
                      {visibleThemesCount < suggestedInterpretationThemes.length && (
                        <button
                          type="button"
                          onClick={() => setVisibleThemesCount(prev => Math.min(prev + THEMES_PAGE_SIZE, suggestedInterpretationThemes.length))}
                          className="px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors"
                        >
                          Show More ({Math.min(THEMES_PAGE_SIZE, suggestedInterpretationThemes.length - visibleThemesCount)} more)
                        </button>
                      )}
                      {visibleThemesCount > THEMES_PAGE_SIZE && (
                        <button
                          type="button"
                          onClick={() => setVisibleThemesCount(THEMES_PAGE_SIZE)}
                          className="px-4 py-2 text-sm font-medium text-gray-600 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors"
                        >
                          Show Less
                        </button>
                      )}
                    </div>
                  )}
                  
                  {/* Showing count indicator */}
                  <p className="text-xs text-gray-400 text-center mt-2">
                    Showing {Math.min(visibleThemesCount, suggestedInterpretationThemes.length)} of {suggestedInterpretationThemes.length} themes
                  </p>
                </div>
              ) : (
                <p className="text-gray-600 text-sm text-center py-4">
                  No patterns found for {displayMetric === "amplitude" ? "amplitude" : "percentage"} metric
                </p>
              )}
            </motion.div>
          )}
        </AnimatePresence>
        <motion.div layout className="relative flex flex-col w-full px-2 pb-2 overflow-auto max-h-[70vh]">
          {/* <Legend /> */}
          <style>{`
            .heatmap-table {
              border-collapse: separate;
              border-spacing: 0;
              width: auto;
            }
            .heatmap-table th,
            .heatmap-table td {
              white-space: nowrap;
            }
            .heatmap-table .channel-cell {
              width: 48px;
              min-width: 48px;
              background: linear-gradient(to right, #f8fafc, #f1f5f9) !important;
            }
            .heatmap-table .band-cell {
              width: auto;
              min-width: 70px;
            }
            .heatmap-table .ratio-cell {
              width: 42px;
              min-width: 42px;
              font-size: 10px;
            }
            .heatmap-table tbody tr:hover {
              background-color: rgba(107, 114, 128, 0.08);
            }
            .heatmap-table tbody tr:hover td.channel-cell {
              background: linear-gradient(to right, #e2e8f0, #e2e8f0) !important;
            }
            .heatmap-table tbody tr:hover td.ratio-cell {
              filter: brightness(0.95);
            }
          `}</style>
          <table
            className="heatmap-table text-sm border-separate border-spacing-0"
          >
            <thead className="sticky top-0 z-20">
              <tr className="bg-slate-100">
                <th 
                  className="channel-cell sticky left-0 z-30 px-3 py-2 border-b-2 border-r-[3px] border-slate-300 text-xs font-bold text-slate-700 tracking-wide bg-slate-100" 
                  rowSpan={showNormativeReference ? 3 : 2}
                >
                  CH
                </th>
                {heatmapBandOrder.map((band, idx) => (
                  <th
                    key={band}
                    className="band-cell px-1 py-1.5 border-b-2 border-slate-300 text-center bg-slate-100 font-semibold text-[11px]"
                    style={{ borderLeft: '4px solid #cbd5e1' }}
                  >
                    {band.toUpperCase()}
                  </th>
                ))}
                {/* Ratio columns header - only in compare mode */}
                {showRatioComparison && ratioHeatmapData.labels.length > 0 && (
                  <th 
                    className="px-2 py-2 border-b-2 border-slate-300 text-center bg-sky-100 font-semibold text-xs text-gray-800"
                    style={{ borderLeft: '6px solid #7dd3fc' }}
                    colSpan={ratioHeatmapData.labels.length}
                  >
                    RATIOS
                  </th>
                )}
              </tr>
              <tr className="bg-slate-50 text-xs text-gray-600">
                {heatmapBandOrder.map((band, idx) => (
                  <th key={`sub-${band}`} className="band-cell p-0 border-b border-slate-200" style={{ borderLeft: '4px solid #cbd5e1' }}>
                    <div className="flex text-center font-semibold text-[9px]">
                      <div className="flex-1 px-1 py-0.5 border-r border-slate-200 bg-blue-50 text-blue-700">MV</div>
                      <div className="flex-1 px-1 py-0.5 bg-green-50 text-green-700">%</div>
                    </div>
                  </th>
                ))}
                {/* Ratio sub-headers - individual ratio labels as 2 lines */}
                {showRatioComparison && ratioHeatmapData.labels.map((label, idx) => {
                  // Split label by '/' to create 2-line header
                  const parts = label.split('/');
                  return (
                    <th 
                      key={`ratio-sub-${label}`} 
                      className="ratio-cell px-0.5 py-0.5 border-b border-sky-200 bg-sky-50 text-gray-800 font-semibold text-center text-[9px] leading-tight"
                      style={{ borderLeft: idx === 0 ? '6px solid #7dd3fc' : '1px solid #e0f2fe' }}
                    >
                      {parts.length > 1 ? (
                        <div className="flex flex-col">
                          <span>{parts[0]}</span>
                          <span className="text-gray-500">{parts[1]}</span>
                        </div>
                      ) : label}
                    </th>
                  );
                })}
              </tr>
              {showNormativeReference && (
                <tr className="bg-indigo-50 text-[9px] text-indigo-600">
                  {heatmapBandOrder.map((band, idx) => (
                    <th key={`norm-${band}`} className="band-cell p-0 border-b border-slate-200" style={{ borderLeft: '4px solid #cbd5e1' }}>
                      <div className="flex text-center text-[9px]">
                        <div className="flex-1 px-1 py-1 border-r border-indigo-100" title={formatAmplitudeNorm(band)}>
                          {formatAmplitudeNorm(band)}
                        </div>
                        <div className="flex-1 px-1 py-1" title={formatPercentageNorm(band)}>
                          {formatPercentageNorm(band)}
                        </div>
                      </div>
                    </th>
                  ))}
                  {/* Empty cells for ratio columns when showing norms */}
                  {showRatioComparison && ratioHeatmapData.labels.map((label, idx) => (
                    <th 
                      key={`norm-ratio-${label}`} 
                      className="ratio-cell px-0.5 py-1 border-b border-sky-200 bg-sky-50 text-[8px] text-center text-gray-400"
                      style={{ borderLeft: idx === 0 ? '6px solid #7dd3fc' : '1px solid #e0f2fe' }}
                    >
                      —
                    </th>
                  ))}
                </tr>
              )}
            </thead>
            <tbody>
              {channelsForTableRows.map((channel, y, channels) => {
                const channelKeyUpper = channel.toUpperCase();
                const channelRatios = ratioHeatmapData.channelData.get(channel);
                return (
                  <tr key={channel} className="transition-colors duration-150">
                    <td 
                      className="channel-cell sticky left-0 z-10 font-bold px-3 py-1.5 text-center border-r-[3px] border-b border-slate-300 text-sm text-slate-800"
                    >
                      {channel}
                    </td>
                    {heatmapBandOrder.map((band, x, bands) => {
                      const { 
                        bandDataObject, 
                        cellTooltipText, 
                        cellStyle,
                        amplitudeBgColor,
                        percentageBgColor,
                        shouldEmphasizeAmplitude,
                        shouldEmphasizePercentage,
                        themeHighlight,
                      } = processCellData(
                          channelKeyUpper,
                          band.toLowerCase(),
                          x,
                          y,
                          bands,
                          channels
                        );
                      return (
                        <td
                          key={band}
                          style={{ ...cellStyle, borderLeft: '4px solid #cbd5e1' }}
                          onClick={() =>
                            handleCellClick(band, channel, cellTooltipText)
                          }
                          className="band-cell p-0 border-b border-slate-200"
                        >
                          {renderSplitCellContent(
                            bandDataObject,
                            amplitudeBgColor,
                            percentageBgColor,
                            shouldEmphasizeAmplitude,
                            shouldEmphasizePercentage,
                            themeHighlight
                          )}
                        </td>
                      );
                    })}
                    {/* Ratio cells - only in compare mode */}
                    {showRatioComparison && ratioHeatmapData.labels.map((label, idx) => {
                      const ratio = channelRatios?.get(label);
                      const value = ratio?.ratio_value;
                      const status = ratio?.status;
                      const bgColor = getRatioCellBgColor(status);
                      
                      return (
                        <td
                          key={`ratio-${label}`}
                          className="ratio-cell px-0.5 py-1 text-center border-b border-sky-100 text-[10px]"
                          style={{ 
                            backgroundColor: bgColor,
                            borderLeft: idx === 0 ? '6px solid #7dd3fc' : '1px solid #e0f2fe'
                          }}
                          title={ratio ? `${label}: ${value?.toFixed(2) ?? 'N/A'} (${status || 'unknown'})` : 'No data'}
                        >
                          {value != null ? value.toFixed(2) : '—'}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
          {showNormativeReference && (
            hasNormativeReferenceData ? (
              <p className="text-[11px] text-gray-500 mt-2">
                Source: {normativeProfileDisplayName}
              </p>
            ) : (
              <p className="text-[11px] text-gray-500 mt-2">
                Normative ranges are not defined for this profile.
              </p>
            )
          )}
          {isLoadingAnalysis && (
            <div
              className="absolute inset-0 bg-radial flex items-center justify-center p-4 md:p-8"
              style={{
                background: "radial-gradient(#fffffff2, transparent 65%)",
              }}
            >
              <svg
                className="animate-spin mr-2 md:mr-3 h-4 w-4 md:h-5 md:w-5 text-blue-500"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
              <p className="text-blue-700 text-xs md:text-sm font-medium">
                Analyzing qEEG data
              </p>
            </div>
          )}
        </motion.div>
      </div>

        {deviation && modalData && (
          <BaseModal
            open={showModal}
            onClose={() => setShowModal(false)}
            title={
              <div className="flex items-center gap-2">
                {modalData.channel} - {modalData.band}
                <span
                  className={`px-2 py-0.5 md:px-3 md:py-1 text-xs rounded-full font-medium ${
                    deviation.status === "diagnosed"
                      ? "bg-green-100 text-green-800"
                      : deviation.status === "irrelevant"
                        ? "bg-gray-100 text-gray-800"
                        : "bg-yellow-100 text-yellow-800"
                  }`}
                >
                  {deviation.status}
                </span>
              </div>
            }
          >
            <div>
              <div className="grid grid-cols-2 gap-3">
                {/* Patient Values */}
                <div className="p-3 bg-blue-50 border border-blue-100 rounded-xl">
                  <p className="text-xs text-blue-600 font-medium mb-2">Patient Values</p>
                  <div className="space-y-1">
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Amplitude</span>
                      <span className="text-sm font-semibold">{modalData.patientAmplitude?.toFixed(2)} µV</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Rhythm %</span>
                      <span className="text-sm font-semibold">{modalData.patientRhythm?.toFixed(2)} %</span>
                    </div>
                  </div>
                </div>
                {/* Normative Thresholds */}
                <div className="p-3 bg-gray-50 border border-gray-200 rounded-xl">
                  <p className="text-xs text-gray-500 font-medium mb-2">Normative Thresholds</p>
                  <div className="space-y-1">
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Amplitude</span>
                      <span className="text-sm font-medium text-gray-700">
                        {modalData.amplitudeMin !== undefined && modalData.amplitudeMax !== undefined 
                          ? `${modalData.amplitudeMin?.toFixed(1)} - ${modalData.amplitudeMax?.toFixed(1)} µV`
                          : '—'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Rhythm %</span>
                      <span className="text-sm font-medium text-gray-700">
                        {modalData.percentageMin !== undefined && modalData.percentageMax !== undefined 
                          ? `${modalData.percentageMin?.toFixed(1)} - ${modalData.percentageMax?.toFixed(1)} %`
                          : '—'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              {deviation.clinician_interpretation && (
                <p className="mt-2 md:mt-3 p-2 md:p-3 bg-blue-50 rounded-lg text-xs md:text-sm text-gray-700">
                  <span className="font-medium text-[#2a5caa]">
                    Interpretation:{" "}
                  </span>
                  {deviation.clinician_interpretation}
                </p>
              )}

              {deviation.diagnosis_category && (
                <p className="mt-2 md:mt-3 p-2 md:p-3 bg-green-50 rounded-lg text-xs md:text-sm text-gray-700">
                  <span className="font-medium text-green-700">Category: </span>
                  {deviation.diagnosis_category}
                </p>
              )}

              <AnimatePresence mode="wait">
                {isEditing && editData ? (
                  <motion.div
                    key="edit-form"
                    className="mt-3 md:mt-5 p-4 md:p-6 bg-gray-50 rounded-xl border border-gray-200 overflow-hidden shadow-sm"
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <div className="space-y-4 md:space-y-5">
                      <div className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-6">
                        <div className="md:col-span-4">
                          <Select
                            label="Status"
                            name="status"
                            defaultValue={editData.status}
                            options={statusOptionsConfig}
                            onChange={handleInputChange}
                            width="w-full"
                            className="mt-3"
                          />
                        </div>
                        <div className="md:col-span-8">
                          <Input
                            label="Diagnosis Category"
                            name="diagnosis_category"
                            value={editData.diagnosis_category}
                            onChange={handleInputChange}
                            placeholder="e.g., ADHD_High_Theta_Low_Beta1_Ratio"
                            className="mt-3 w-full"
                          />
                        </div>
                      </div>

                      <Textarea
                        label="Clinical Interpretation"
                        name="clinician_interpretation"
                        value={editData.clinician_interpretation}
                        onChange={handleInputChange}
                        rows={3}
                        placeholder="Enter your clinical interpretation of this deviation..."
                      />

                      <div className="flex flex-wrap gap-3 pt-2">
                        <Button
                          onClick={handleSave}
                          className="px-4 py-2 bg-[#2a5caa] hover:bg-[#234b8a] text-white rounded-lg text-sm font-medium shadow-sm transition-colors"
                        >
                          Save Changes
                        </Button>
                        <Button
                          onClick={handleCancelEdit}
                          className="px-4 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded-lg text-sm font-medium shadow-sm transition-colors"
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  </motion.div>
                ) : (
                  <motion.div
                    key="edit-button"
                    className="mt-3 md:mt-4"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.2 }}
                  >
                    <Button
                      onClick={handleEdit}
                      className="px-3 py-1 md:px-4 md:py-1.5 bg-[#2a5caa] hover:bg-[#234b8a] text-white text-xs md:text-sm font-medium rounded-lg shadow-sm hover:shadow-md transition-colors"
                    >
                      Review/Edit
                    </Button>
                  </motion.div>
                )}
              </AnimatePresence>
              <AnimatePresence mode="wait">
                {(isEditing || interpretationMatches) && (
                  <motion.div
                    key="interpretations"
                    className="mt-3 md:mt-5"
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <h5 className="font-semibold text-[#1c3f70] text-sm md:text-base mb-2 md:mb-3">
                      Suggested Diagnostics
                    </h5>
                    {isLoadingInterpretationsMatches && (
                      <div className="flex items-center space-x-2 p-3 bg-gray-50 rounded-lg">
                        <div className="w-3 h-3 md:w-4 md:h-4 rounded-full bg-[#2a5caa] animate-pulse"></div>
                        <p className="text-xs md:text-sm text-gray-600">
                          Loading matches...
                        </p>
                      </div>
                    )}

                    {!isLoadingInterpretationsMatches &&
                      interpretationMatches &&
                      (Array.isArray(interpretationMatches) ? (
                        interpretationMatches.length > 0 ? (
                          <ul className="flex flex-col gap-2 max-h-64 overflow-y-auto">
                            {[...interpretationMatches]
                              .map((match) => {
                                // Look up percentage from suggested themes
                                const themeWithPercentage = suggestedInterpretationThemes.find(
                                  (t) => t.theme_id === match.id || t.id === match.id
                                );
                                return { ...match, percentage: themeWithPercentage?.percentage_exceeding };
                              })
                              .sort((a, b) => (b.percentage ?? -1) - (a.percentage ?? -1))
                              .map((match, index) => {
                              const percentage = match.percentage;
                              
                              // Color coding based on percentage
                              let badgeColor = "bg-gray-100 text-gray-700";
                              if (percentage !== undefined) {
                                if (percentage >= 75) {
                                  badgeColor = "bg-green-100 text-green-800";
                                } else if (percentage >= 50) {
                                  badgeColor = "bg-blue-100 text-blue-800";
                                } else if (percentage >= 25) {
                                  badgeColor = "bg-amber-100 text-amber-800";
                                }
                              }
                              
                              return (
                                <motion.li
                                  key={index}
                                  className="bg-white rounded-lg border border-gray-200 hover:border-blue-300 hover:shadow-sm transition-all"
                                  initial={{ opacity: 0, x: -10 }}
                                  animate={{ opacity: 1, x: 0 }}
                                  transition={{ delay: index * 0.05 }}
                                >
                                  <NavLink
                                    to={`/interpretation-themes/themes/${match.id}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="p-3 flex items-start gap-3"
                                  >
                                    <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-bold flex-shrink-0 ${badgeColor}`}>
                                      {percentage !== undefined ? `${percentage.toFixed(0)}%` : "Match"}
                                    </span>
                                    <p className="text-sm text-gray-700 leading-snug flex-1">
                                      {match.interpretation_text}
                                    </p>
                                  </NavLink>
                                </motion.li>
                              );
                            })}
                          </ul>
                        ) : (
                          <p className="text-xs md:text-sm text-gray-500 p-3 bg-gray-50 rounded-lg">
                            No matching diagnostics found for this deviation.
                          </p>
                        )
                      ) : (
                        <p className="text-xs md:text-sm text-gray-500 p-3 bg-gray-50 rounded-lg">
                          {interpretationMatches.message ||
                            "No matches found or error."}
                        </p>
                      ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </BaseModal>
        )}

        {/* Theme Details Modal */}
        <BaseModal
          open={showThemeDetailsModal}
          onClose={() => setShowThemeDetailsModal(false)}
          title={selectedThemeForModal?.interpretation_text || "Theme Details"}
          maxWidth="lg"
          hideFooter={true}
        >
          {selectedThemeForModal && (
            <div>
              {/* Summary Stats */}
              <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="text-gray-600">Match (of available data):</span>
                    <span className="ml-2 font-semibold text-[#1c3f70]">
                      {selectedThemeForModal.percentage_exceeding?.toFixed(1)}%
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-600">Exceeding norm:</span>
                    <span className="ml-2 font-semibold text-[#1c3f70]">
                      {selectedThemeForModal.exceeding_norm}/
                      {selectedThemeForModal.total_combinations}
                    </span>
                    <span className="ml-1 text-xs text-gray-500">combinations</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Source:</span>
                    <span className="ml-2 font-semibold text-[#1c3f70]">
                      {selectedThemeForModal.source_type}
                    </span>
                  </div>
                  {/* Coverage information */}
                  {selectedThemeForModal.coverage_percentage !== undefined && (
                    <div>
                      <span className="text-gray-600">Data Coverage:</span>
                      <span className={clsx(
                        "ml-2 font-semibold",
                        selectedThemeForModal.coverage_percentage < 100 ? "text-amber-600" : "text-green-600"
                      )}>
                        {selectedThemeForModal.coverage_percentage?.toFixed(0)}%
                      </span>
                      {selectedThemeForModal.coverage_percentage < 100 && (
                        <span className="ml-1 text-xs text-gray-500">
                          ({selectedThemeForModal.total_combinations}/{selectedThemeForModal.theoretical_combinations} of theoretical)
                        </span>
                      )}
                    </div>
                  )}
                </div>
                
                {/* Missing channels warning */}
                {selectedThemeForModal.missing_channels && selectedThemeForModal.missing_channels.length > 0 && (
                  <div className="mt-3 p-2 bg-amber-50 rounded border border-amber-200">
                    <p className="text-sm text-amber-700 font-medium mb-1">
                      ⚠ Missing Channels from Input Data:
                    </p>
                    <div className="flex flex-wrap gap-1">
                      {selectedThemeForModal.missing_channels.map((ch) => (
                        <span key={ch} className="px-2 py-0.5 bg-white rounded text-amber-800 text-xs border border-amber-300">
                          {ch}
                        </span>
                      ))}
                    </div>
                    <p className="text-xs text-amber-600 mt-1">
                      These channels are expected for this interpretation theme but were not found in the session data.
                    </p>
                  </div>
                )}
              </div>

              {/* Combinations Table */}
              <div className="overflow-x-auto mb-4">
                <table className="min-w-full text-sm border-collapse">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="px-3 py-2 text-left font-semibold text-gray-700 border-b">
                        Channel
                      </th>
                      <th className="px-3 py-2 text-left font-semibold text-gray-700 border-b">
                        Band
                      </th>
                      <th className="px-3 py-2 text-right font-semibold text-gray-700 border-b">
                        Value
                      </th>
                      <th className="px-3 py-2 text-right font-semibold text-gray-700 border-b">
                        Norm
                      </th>
                      <th className="px-3 py-2 text-center font-semibold text-gray-700 border-b">
                        Exceeds
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {(selectedThemeForModal.all_combinations || []).map(
                      (combo, idx) => (
                        <tr
                          key={idx}
                          className={clsx(
                            "border-b",
                            !combo.has_data ? "bg-gray-100 opacity-60" :
                            combo.exceeds_norm ? "bg-red-50" : "bg-white"
                          )}
                        >
                          <td className={clsx(
                            "px-3 py-2 font-medium",
                            !combo.has_data && "text-gray-500"
                          )}>
                            <BrainRegionText channel={combo.channel}>
                              {combo.channel}
                            </BrainRegionText>
                          </td>
                          <td className={clsx(
                            "px-3 py-2",
                            combo.has_data ? "text-gray-700" : "text-gray-500"
                          )}>
                            {combo.frequency_band}
                          </td>
                          <td className="px-3 py-2 text-right font-semibold">
                            {combo.has_data ? combo.deviation_value?.toFixed(2) : (
                              <span className="text-gray-400 italic">—</span>
                            )}
                          </td>
                          <td className="px-3 py-2 text-right text-gray-600">
                            {combo.has_data ? combo.norm_value?.toFixed(2) : (
                              <span className="text-gray-400 italic">—</span>
                            )}
                          </td>
                          <td className="px-3 py-2 text-center">
                            {!combo.has_data ? (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-gray-200 text-gray-500">
                                No data
                              </span>
                            ) : combo.exceeds_norm ? (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-red-100 text-red-800">
                                Yes
                              </span>
                            ) : (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-gray-100 text-gray-600">
                                No
                              </span>
                            )}
                          </td>
                        </tr>
                      )
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </BaseModal>
    </motion.div>
  );
};

export default HeatmapVisualization;
